import { Button, Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { NumericFormat } from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import GlobalContext from "../../context/GlobalContext";
import { getUserInfo, updateSalary } from "../../api/UsersApi";
import BoxSkeleton from "../../components/utilities/BoxSkeleton";
import BackButton from "../../components/button/BackButton";
import { Typography } from "../../components/utilities/Typhograpy";
import swal from "../../services/swalConfig";

import { tr } from "../../i18n";
const UsersEditSalary = () => {
  const isMounted = useRef(true);
  const location = useLocation();
  const personnelId = location.state?.personnel_id || null;
  const [personelData, setPersonelData] = useState(null);
  const { user, setLoading } = useContext(GlobalContext);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const loadPersonnelInformation = async () => {
    setPersonelData(null);

    try {
      const response = await getUserInfo(personnelId);

      if (response.status === 200) {
        setPersonelData(response.data);
      } else if (response.status === 404) {
        setPersonelData([]);
      }
    } catch (error) {
      setPersonelData(null);
    }
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    loadPersonnelInformation();
  }, [user]);

  return (
    <Row>
      <Col lg={7}>
        <div className="box-shadow p-4 bg-white rounded-4">
          {personelData == null ? (
            <BoxSkeleton count="6" size="12" height="20px" />
          ) : (
            <Form
              onSubmit={handleSubmit(async (data) => {
                try {
                  setLoading(true);

                  const newData = {
                    company_id: user.workspace.company_id,
                    personnel_id: personnelId,
                    sallary: data.sallary,
                  };

                  const response = await updateSalary(newData);

                  response.status === 400
                    ? Object.entries(response.errors).forEach(
                        ([key, message]) =>
                          setError(key, { type: "manual", message }),
                      )
                    : swal.fire({
                        title:
                          response.status === 200
                            ? tr("users:congratulations")
                            : tr("common:errors.oops"),
                        text: response.message,
                        icon: response.status === 200 ? "success" : "error",
                        showCancelButton: false,
                      });
                } catch (error) {
                  swal.fire({
                    title: tr("common:errors.oops"),
                    text: tr("users:something_wrong_try_logging_in_later"),
                    icon: "error",
                    showCancelButton: false,
                  });
                } finally {
                  setLoading(false);
                }
              })}
            >
              <div className="mb-4">
                <Typography variant="body2" fontWeight={600}>
                  {tr("users:salary_adjustment")}
                </Typography>
                <Typography variant="caption" style={{ opacity: 0.75 }}>
                  {tr("users:salary_updates_are_handled_separately_because")}
                </Typography>
              </div>

              <Form.Group as={Row} className="mb-2">
                <Form.Label column sm="3">
                  <Typography variant="form">{tr("users:id")}</Typography>
                </Form.Label>
                <Col sm="9">
                  <Form.Control
                    plaintext
                    readOnly
                    defaultValue={personelData.internal_id || "-"}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-2">
                <Form.Label column sm="3">
                  <Typography variant="form">{tr("shared:name")}</Typography>
                </Form.Label>
                <Col sm="9">
                  <Form.Control
                    plaintext
                    readOnly
                    defaultValue={personelData.personal_name}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="3">
                  <Typography variant="form">{tr("users:current_salary")}</Typography>
                </Form.Label>
                <Col sm="9">
                  <Form.Control
                    plaintext
                    readOnly
                    defaultValue={`${personelData.internal_sallary_currency_symbol || ""} ${Number(personelData.internal_sallary_amount || 0).toLocaleString("id-ID")}`}
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm="3">
                  <Typography variant="form">{tr("users:new_salary")}</Typography>
                </Form.Label>
                <Col sm="9">
                  <Controller
                    name="sallary"
                    control={control}
                    rules={{ required: tr("users:the_salary_is_required") }}
                    defaultValue={personelData.internal_sallary_amount || ""}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <div style={{ position: "relative" }}>
                        <span
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "12px",
                            transform: "translateY(-50%)",
                            color: "#6c757d",
                            pointerEvents: "none",
                            zIndex: 2,
                          }}
                        >
                          {personelData.internal_sallary_currency_symbol ||
                            "Rp"}
                        </span>
                        <NumericFormat
                          value={value}
                          thousandSeparator="."
                          decimalSeparator=","
                          allowNegative={false}
                          onValueChange={(values) => onChange(values.value)}
                          className={`form-control ${error ? "is-invalid" : ""}`}
                          placeholder={tr("common:breadcrumb.salary")}
                          style={{ paddingLeft: "42px" }}
                        />
                      </div>
                    )}
                  />
                  {errors.sallary ? (
                    <small className="text-danger">
                      {errors.sallary.message}
                    </small>
                  ) : (
                    <Typography variant="caption" style={{ opacity: 0.75 }}>
                      {tr("users:employee_will_receive_notification_and_email")}
                    </Typography>
                  )}
                </Col>
              </Form.Group>

              <div className="mt-4 d-flex justify-content-between">
                <BackButton params={{ personnel_id: personnelId }} />
                <Button type="submit" className="btn btn-primary">
                  <FontAwesomeIcon icon={faCheckCircle} /> {" "}{tr("users:update_salary")}
                </Button>
              </div>
            </Form>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default UsersEditSalary;
